import React, { useMemo } from 'react';
import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectStatusChipConfigForDomain, selectStatusChipConfig } from '../../redux/slices/statusDefinitionsSlice';

// Generic, status-agnostic placeholder shown only for the brief window before
// status definitions have loaded from the DB — not per-status data.
const FALLBACK = { color: '#4A5568', bg: '#EDF2F7' };

// Status colors are admin-picked (Status Settings), one {bg, color} hex pair
// per status, stored as-is in the DB — there's no separate dark-mode value
// in that data model, and asking every admin to re-pick a second color per
// status for dark mode isn't reasonable. Instead, in dark mode, the same
// hue is re-rendered as a deep tinted surface + a bright tinted text color
// (same idea as KpiCard's light-pastel → dark-tint mapping, just computed
// algorithmically here since these colors aren't a fixed known set).
const hexToRgb = (hex) => {
  const h = String(hex).trim().replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    switch (max) {
      case rn: h = ((gn - bn) / d) % 6; break;
      case gn: h = (bn - rn) / d + 2; break;
      default: h = (rn - gn) / d + 4; break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
};

const hslToHex = (h, s, l) => {
  const sn = s / 100, ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Exported for the audit/future reuse (e.g. if another admin-colored-hex
// surface needs the same dark-mode treatment) rather than kept private.
export const toDarkSafeChipColors = ({ bg, color }) => {
  const bgRgb = hexToRgb(bg);
  const colorRgb = hexToRgb(color);
  // Not a plain hex (e.g. already an rgba()/named CSS color) — leave both
  // values untouched rather than guess, since that's outside what this
  // admin-configurable color picker is expected to ever store.
  if (!bgRgb || !colorRgb) return { bg, color };

  const bgHsl = rgbToHsl(bgRgb);
  const colorHsl = rgbToHsl(colorRgb);

  return {
    bg: hslToHex(bgHsl.h, Math.min(Math.max(bgHsl.s, 30), 55), 16),
    color: hslToHex(colorHsl.h, Math.min(Math.max(colorHsl.s, 45), 85), 72),
  };
};

// `domain` should always be passed when known — the same status key can
// exist in multiple domains with different colors (e.g. "pending" in
// item/deliveryReceipt/payment), so without it colors can come from the
// wrong domain. Omitting `domain` falls back to a cross-domain merge that's
// only reliable for keys that are unique across every domain.
const StatusChip = ({ status, domain, size = 'small' }) => {
  const theme = useTheme();
  const domainConfig = useSelector(domain ? selectStatusChipConfigForDomain(domain) : selectStatusChipConfig);
  const dynamic = domainConfig[status];
  const colorConfig = dynamic || FALLBACK;
  const label = dynamic?.label || status;

  const resolved = useMemo(
    () => (theme.palette.mode === 'dark' ? toDarkSafeChipColors(colorConfig) : colorConfig),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.mode, colorConfig.bg, colorConfig.color],
  );

  return (
    <Chip
      label={label}
      size={size}
      sx={{
        bgcolor: resolved.bg,
        color: resolved.color,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.6875rem' : '0.75rem',
        height: size === 'small' ? 22 : 26,
        border: 'none',
        '& .MuiChip-label': { px: 1.25 },
      }}
    />
  );
};

export default StatusChip;
